import {
  calculationsApi,
  FileOperationOUT,
  getLocalAuthHeader,
} from "./../../api-client/index";
import {
  BuildingOUT,
  BuildingReportOUT,
  buildingsApi,
  BuildingTypeOUT,
  datasetCheckerApi,
  DatasetCheckerOUT,
  ExportStatusEnum,
  LicenseKindType,
  PagedBuildingOUT,
  reportBuildingApi,
  userBuildingApi,
  UserBuildingOUT,
  UserRoleOUT,
} from "api-client";
import { makeAutoObservable } from "mobx";
import {
  BuildingMappingStatus,
  CalculationsStatus,
  ExportedCertifications,
  SourceFiles,
  OrganisationBuilding,
  SharedBuildings,
  MenuConfirmModal,
} from "./types";
import { AxiosResponse } from "axios";
import { sidebarMenuStore } from "../../features/SidebarMenu/SidebarMenuStore.ts";
import { conformityStore } from "../ConformityStore.ts";

export class BuildingStore {
  currentBuilding: BuildingOUT | undefined;
  buildingMappingStatus: BuildingMappingStatus = {
    hasMappedObjects: false,
    hasComplientsObjects: false,
    hasReport: false,
    loading: true,
  };
  buildingTypes: BuildingTypeOUT[] = [];
  sharedBuildingsUserRoles: UserRoleOUT[] = [];
  sharedBuildings: SharedBuildings = {
    items: [],
    loading: true,
  };
  isSharedBuilding = false;
  buildingsLoading = false;
  sourceFiles: SourceFiles = {
    items: [],
    loading: true,
  };
  organisationsBuildings: OrganisationBuilding[] = [];
  noBuilding: boolean | undefined = undefined;
  buildingID: string | undefined;
  exportedCertifications: ExportedCertifications = {
    loading: true,
    items: [],
  };
  calculationsStatus: CalculationsStatus = { id: "", inProgress: false };
  filesCalculationStatus: FileOperationOUT[] | undefined = undefined;
  interval: NodeJS.Timer | undefined;
  openMenuConfrimModal: MenuConfirmModal | undefined = undefined;

  setBuildingID(buildingID: string | undefined) {
    this.buildingID = buildingID;
  }

  setBuildingTypes(buildingTypes: BuildingTypeOUT[]) {
    this.buildingTypes = buildingTypes;
  }

  setSharedBuildingsUserRoles(sharedBuildingsUserRoles: UserRoleOUT[]) {
    this.sharedBuildingsUserRoles = sharedBuildingsUserRoles;
  }

  setSharedBuildings(sharedBuildings: SharedBuildings) {
    this.sharedBuildings = sharedBuildings;
  }

  setIsSharedBuilding(isSharedBuilding: boolean) {
    this.isSharedBuilding = isSharedBuilding;
  }

  setBuildingsLoading(buildingsLoading: boolean) {
    this.buildingsLoading = buildingsLoading;
  }

  setSourceFiles(sourceFiles: SourceFiles) {
    this.sourceFiles = sourceFiles;
  }

  setOrganisationsBuildings(organisationsBuildings: OrganisationBuilding[]) {
    this.organisationsBuildings = organisationsBuildings;
  }

  setNoBuilding(noBuilding: boolean | undefined) {
    this.noBuilding = noBuilding;
  }

  setCurrentBuilding(currentBuilding: BuildingOUT | undefined) {
    let buildingChanged = false;
    if (currentBuilding && currentBuilding?.id !== this.currentBuilding?.id) {
      buildingChanged = true;
    }
    this.currentBuilding = currentBuilding;
    if (buildingChanged) {
      this.checkIfUserIsGuest(this.currentBuilding?.id);
    }
  }

  setBuildingMappingStatus(buildingMappingStatus: BuildingMappingStatus) {
    this.buildingMappingStatus = buildingMappingStatus;
  }

  setExportedCertifications(exportedCertifications: ExportedCertifications) {
    this.exportedCertifications = exportedCertifications;
  }

  setCalculationsLoading(calculationsStatus: CalculationsStatus) {
    this.calculationsStatus = calculationsStatus;
  }

  setFilesCalculationStatus(
    filesCalculationStatus: FileOperationOUT[] | undefined
  ) {
    this.filesCalculationStatus = filesCalculationStatus;
  }

  setOpenMenuCheckerModal(openMenuCheckerModal: MenuConfirmModal | undefined) {
    this.openMenuConfrimModal = openMenuCheckerModal;
  }

  resetBuildingMappingStatus() {
    this.setBuildingMappingStatus({
      hasMappedObjects: false,
      hasComplientsObjects: false,
      hasReport: false,
      loading: true,
    });
  }

  async fetchCheckDatasetCompliance(
    building_id: string | undefined,
    certification_id: string | undefined | null,
    conformity_id: string | undefined | null,
    use_simplified_version_lca?: boolean
  ): Promise<DatasetCheckerOUT | void> {
    if (!building_id || !certification_id || !conformity_id) return;
    const authHeader = getLocalAuthHeader();
    return await datasetCheckerApi
      .inventoryApiV1RoutersDatasetCheckerCheckDatasetCompliance(
        {
          building_id: building_id,
          certification_id: certification_id,
          conformity_id: conformity_id,
          din_category_filter: { use_simplified_version_lca },
        },
        authHeader
      )
      .then((response) => response.data)
      .catch((err) =>
        console.error(err, "error.DatasetCheckerCheckDatasetCompliance")
      );
  }

  async getCurrentBuildingDetails(building_id?: string | undefined) {
    const buildingId = building_id ?? this.currentBuilding?.id;
    if (!buildingId) return;
    const authHeader = getLocalAuthHeader();
    return buildingsApi
      .concularApiV1RoutersBuildingsGetOrgBuilding(buildingId, authHeader)
      .then((response) => response.data)
      .catch((err) => console.error(err, "getCurrentBuildingDetails.error"));
  }

  async getBuildingMappingStatus(building_id?: string | undefined) {
    const buildingId = building_id ?? this.currentBuilding?.id;
    if (!buildingId) return;
    const authHeader = getLocalAuthHeader();
    await buildingsApi
      .concularApiV1RoutersBuildingsMappingStatus(buildingId, authHeader)
      .then((response) => {
        const data = response.data;
        if (data.building_id === buildingId)
          this.setBuildingMappingStatus({
            hasMappedObjects: data.has_mapped_objects,
            hasComplientsObjects: data.has_compliant_matches,
            hasReport: data.has_mapped_objects && data.has_compliant_matches,
            loading: false,
          });
      })
      .catch((err) => console.error(err, "BuildingsMappingStatus"));
  }

  async getBuildingTypes() {
    const authHeader = getLocalAuthHeader();
    buildingsApi
      .concularApiV1RoutersBuildingsGetAllBuildingTypes(100, 0, authHeader)
      .then((response) => {
        if (response.data.items) {
          this.setBuildingTypes(response.data.items);
        } else {
          console.error("getBuildingTypes.response", response);
        }
      })
      .catch((err) => console.error("getBuildingTypes", err));
  }

  async getAllSourceFiles() {
    const authHeader = getLocalAuthHeader();
    if (!this.currentBuilding?.id) return;
    await buildingsApi
      .concularApiV1RoutersBuildingsGetAllMaterialResources(
        this.currentBuilding?.id,
        authHeader
      )
      .then((response: AxiosResponse) => {
        this.setSourceFiles({ items: response.data, loading: false });
      })
      .catch((err) => {
        console.error("getAllMaterialResources", err);
        this.setSourceFiles({
          ...this.sourceFiles,
          loading: false,
        });
      });
  }

  async getSharedBuildingsUserRoles() {
    const authHeader = getLocalAuthHeader();
    userBuildingApi
      .concularApiV1RoutersUserBuildingsGetAllRoles(authHeader)
      .then((response) => {
        if (response.data) {
          this.setSharedBuildingsUserRoles(response.data);
        } else {
          console.error("getUseruserBuildingRoles.response", response);
        }
      })
      .catch((err) => console.error("getUseruserBuildingRoles", err));
  }

  async getSharedBuildings() {
    const authHeader = getLocalAuthHeader();
    await userBuildingApi
      .concularApiV1RoutersUserBuildingsGetUserBuildings(authHeader)
      .then(async (response: AxiosResponse<UserBuildingOUT[]>) => {
        const userBuilding = response?.data;
        if (!userBuilding) return console.error("getUserBuildings", response);
        this.setSharedBuildings({ items: userBuilding, loading: false });
        await this.getSharedBuildingsUserRoles();
      })
      .catch((err) => {
        console.error("getUserBuildings", err);
      })
      .finally(() =>
        this.setSharedBuildings({ ...this.sharedBuildings, loading: false })
      );
  }

  async getOrganisationBuildings(org_id?: string) {
    const currentOrgID = org_id ?? sidebarMenuStore.currentOrganisation?.id;
    const authHeader = getLocalAuthHeader();
    if (!currentOrgID) {
      return console.error("undefined.org.id-organisationsBuildings");
    }
    return await buildingsApi
      .concularApiV1RoutersBuildingsGetAllOrgBuildings(
        currentOrgID,
        200,
        0,
        authHeader
      )
      .then((response: AxiosResponse<PagedBuildingOUT, Error>) => {
        const responseItems = response.data.items;
        this.updateOrganisationBuildings(currentOrgID, responseItems);
        this.findAndUpdateCurrentBuilding(responseItems);
        return response.data;
      })
      .catch((err) => console.error("getAllOrganizationsBuildings", err));
  }

  private updateOrganisationBuildings(
    currentOrgID: string,
    responseItems: BuildingOUT[]
  ) {
    const foundCurrentOrgBuildings = this.organisationsBuildings.find(
      (rec) => rec.orgId === currentOrgID
    );
    if (foundCurrentOrgBuildings) {
      this.updateCurrentOrganisationBuildings(currentOrgID, responseItems);
    } else {
      this.setOrganisationsBuildings([
        ...this.organisationsBuildings,
        {
          loading: false,
          items: responseItems,
          orgId: currentOrgID,
        },
      ]);
    }
  }

  private findAndUpdateCurrentBuilding(responseItems: BuildingOUT[]) {
    if (this.buildingID && responseItems) {
      const foundCurrentBuilding = responseItems.find(
        (el) => el.id === this.buildingID
      );
      foundCurrentBuilding && this.setCurrentBuilding(foundCurrentBuilding);
    }
  }

  private updateCurrentOrganisationBuildings(
    orgId: string,
    buildings: BuildingOUT[]
  ) {
    this.organisationsBuildings.forEach((rec) => {
      if (rec.orgId === orgId) {
        rec.items = [...buildings];
      }
    });
    this.setOrganisationsBuildings([...this.organisationsBuildings]);
  }

  getCurrentOrganisationBuildings = (org_id: string) => {
    return this.organisationsBuildings.find((item) => item.orgId === org_id)
      ?.items;
  };

  hasAnyBuildings() {
    return (
      this.organisationsBuildings.filter((rec) => rec.items.length > 0).length >
      0
    );
  }

  checkIfUserIsGuest(building_id?: string) {
    //on change current building we check if user is a guest
    const sharedBuilding = this.sharedBuildings.items.find(
      (building) => building.building.id === building_id
    );
    if (sharedBuilding) {
      return this.setIsSharedBuilding(true);
    }
    this.setIsSharedBuilding(false);
  }

  async getBuildingExportCertifications(
    status?: ExportStatusEnum
  ): Promise<void | BuildingReportOUT[]> {
    const authHeader = getLocalAuthHeader();
    if (!this.buildingID) return;
    this.setExportedCertifications({
      ...this.exportedCertifications,
      loading: true,
    });
    return await reportBuildingApi
      .reportApiV1RoutersBuildingGetBuildingExportedCertifications(
        this.buildingID,
        status,
        authHeader
      )
      .then((response) => response.data)
      .catch((err) => console.error("GetBuildingExportedCertifications", err))
      .finally(() => {
        this.setExportedCertifications({
          ...this.exportedCertifications,
          loading: false,
        });
      });
  }

  async deleteBuildingExportedCertification(report_id: string) {
    const authHeader = getLocalAuthHeader();
    if (!this.buildingID) return;
    return await reportBuildingApi
      .reportApiV1RoutersBuildingDeleteBuildingExportedCertification(
        this.buildingID,
        report_id,
        authHeader
      )
      .then((response) => response.data)
      .catch((err) =>
        console.error("deleteBuildingExportedCertification", err)
      );
  }

  async fetchExportMasterToolLike() {
    const authHeader = getLocalAuthHeader();
    if (
      !this.currentBuilding ||
      !this.currentBuilding.certification_id ||
      !this.currentBuilding.conformity_id
    )
      return;

    return await reportBuildingApi
      .reportApiV1RoutersBuildingExportMasterToolLike(
        this.currentBuilding.id,
        {
          certification_id: this.currentBuilding.certification_id,
          conformity_id: this.currentBuilding.conformity_id,
        },
        authHeader
      )
      .then((response) => response.data)
      .catch((err) => console.error("fetchExportMasterToolLike", err));
  }

  getBuildingCertificationName(certification_id?: string | null) {
    const certId = certification_id || this.currentBuilding?.certification_id;
    return conformityStore.certificationOptions.find(
      (item) => item.id === certId
    )?.name;
  }

  checkIfNoBuildingDashboard() {
    const allFilesCount = this.sourceFiles.items.length;
    const activeFileIDsList = this.getActiveFileIDs();
    return (
      allFilesCount == 0 ||
      activeFileIDsList.length == 0 ||
      !this.buildingMappingStatus.hasMappedObjects
    );
  }

  getActiveFileIDs() {
    return this.sourceFiles.items
      .filter((item) => item.reporting_status === "ACTIVE")
      .map((item) => item.id);
  }

  getBuildingFilesCount() {
    return this.sourceFiles.items.length;
  }

  getModuleNamesPerCertification() {
    const generalModules = ["A1-A3", "B4", "C3", "C4"];
    const dgnb2018 = [...generalModules, "D"];
    let result = generalModules;

    if (!this.currentBuilding?.certification_id) {
      return result;
    }
    const certificationName = this.getBuildingCertificationName(
      this.currentBuilding?.certification_id
    );
    if (certificationName?.includes("2018")) {
      result = dgnb2018;
    }
    return result;
  }

  getXKTFileUrl(file_id: string | undefined) {
    if (!file_id) return;
    return this.sourceFiles.items.find((item) => item.id === file_id)
      ?.file_processed_xkt;
  }

  getXKTFileStatus(file_id: string | undefined) {
    if (!file_id) return;
    return this.sourceFiles.items.find((item) => item.id === file_id)
      ?.xkt_status;
  }

  private async reCheckCalculationsStatus(id?: string) {
    if (!id) return;
    this.interval = setInterval(
      async () => {
        await this.getBuildingCalculationsStatus(id);
        if (!this.calculationsStatus.inProgress) clearInterval(this.interval);
      },
      this.calculationsStatus.inProgress ? 5000 : 10000
    );
  }

  async getBuildingCalculationsStatus(id?: string) {
    const authHeader = getLocalAuthHeader();
    const currentID = id ?? this.buildingID;
    if (!currentID) return;

    const previousBuildingCalculationStatus =
      this.calculationsStatus.inProgress &&
      currentID !== this.calculationsStatus.id;

    if (previousBuildingCalculationStatus) {
      /* Clear last interval on change building */
      clearInterval(this.interval);
      this.setCalculationsLoading({ id: undefined, inProgress: false });
      this.setFilesCalculationStatus([]);
    }

    return await calculationsApi
      .concularApiV1RoutersCalculationsGetCalculationsStatus(
        "building",
        currentID,
        authHeader
      )
      .then((response) => {
        const currentCalculationsState = response.data.status === "IN_PROGRESS";

        const currentBuildingCalculationsStatusChanged =
          currentCalculationsState && !this.calculationsStatus.inProgress;
        if (currentBuildingCalculationsStatusChanged) {
          /* Run interval to recheck calculations status */
          this.reCheckCalculationsStatus(currentID);
        }

        this.setCalculationsLoading({
          id: currentID,
          inProgress: currentCalculationsState,
        });
        this.setFilesCalculationStatus(response.data.file_operations);
      });
  }

  async getPPDFReport() {
    if (!this.buildingID) return "";
    const authHeader = getLocalAuthHeader();
    await reportBuildingApi
      .reportApiV1RoutersBuildingGetBuildingPdfReport(this.buildingID, {
        ...authHeader,
        responseType: "blob",
      })
      .catch((err) => console.error(err, "GetBuildingPdfReport"));
  }

  downloadPDF(data: string) {
    try {
      // Convert string to a Blob
      const blob = new Blob([data], { type: "application/pdf" });
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Invalid Blob data");
      }
      // Create a URL for the Blob
      const url = window.URL.createObjectURL(blob);
      // Create an anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = "building_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error handling PDF download:", error);
    }
  }

  buildingHasKeineCertification() {
    const isKeineCertificate =
      this.currentBuilding?.certification_id ===
      conformityStore.certificationOptions.find((item) => item.name === "Keine")
        ?.id;
    return isKeineCertificate;
  }

  // A building is archived once it has been assigned the read-only building licence.
  get isCurrentBuildingArchived() {
    return this.currentBuilding?.license?.kind === LicenseKindType.ReadOnly;
  }

  async initialFetch() {
    //get building types, shared buildings list and all buildings of all organisations on loading the app
    await this.getBuildingTypes();
    await this.getSharedBuildings();
  }

  static instance: BuildingStore;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): BuildingStore {
    if (!BuildingStore.instance) {
      BuildingStore.instance = new BuildingStore();
    }
    return BuildingStore.instance;
  }
}

export const buildingStore = BuildingStore.getInstance();
